import React, { useState, useEffect } from 'react';
import { 
  Mail, 
  MessageSquare, 
  Share2, 
  Users, 
  Copy, 
  Plus, 
  Trash2, 
  Send,
  Gift,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

const InviteFriends = () => {
  const [referralData, setReferralData] = useState(null);
  const [myInvites, setMyInvites] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingInvites, setSendingInvites] = useState(false);
  const [message, setMessage] = useState('');
  
  // Single invite form
  const [singleInvite, setSingleInvite] = useState({
    name: '',
    email: '',
    phone: '',
    personal_message: ''
  });
  
  // Bulk invite form
  const [contacts, setContacts] = useState([
    { name: '', email: '', phone: '', personal_message: '' }
  ]);
  const [inviteMethod, setInviteMethod] = useState('both');

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '/api';

  useEffect(() => {
    fetchReferralData();
    fetchMyInvites();
  }, []);

  const fetchReferralData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${backendUrl}/referral/my-code`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setReferralData(data);
      }
    } catch (error) {
      console.error('Error fetching referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyInvites = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${backendUrl}/referral/my-invites`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMyInvites(data);
      }
    } catch (error) {
      console.error('Error fetching invitations:', error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setMessage('Link copied to clipboard!');
    setTimeout(() => setMessage(''), 3000);
  };

  const sendSingleInvite = async () => {
    if (!singleInvite.name || (!singleInvite.email && !singleInvite.phone)) {
      setMessage('Please provide name and either email or phone number');
      return;
    }

    setSendingInvites(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${backendUrl}/referral/invite-friend`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(singleInvite)
      });

      if (response.ok) {
        const data = await response.json();
        setMessage(`Invitation sent successfully via ${data.methods.join(' and ')}!`);
        setSingleInvite({ name: '', email: '', phone: '', personal_message: '' });
        fetchMyInvites(); // Refresh stats
      } else {
        setMessage('Error sending invitation. Please try again.');
      }
    } catch (error) {
      setMessage('Error sending invitation. Please try again.');
    } finally {
      setSendingInvites(false);
    }
  };

  const sendBulkInvites = async () => {
    const validContacts = contacts.filter(c => c.name && (c.email || c.phone));
    if (validContacts.length === 0) {
      setMessage('Please add at least one valid contact');
      return;
    }

    setSendingInvites(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${backendUrl}/referral/invite-bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          contacts: validContacts,
          method: inviteMethod
        })
      });

      if (response.ok) {
        const data = await response.json();
        setMessage(data.message);
        setContacts([{ name: '', email: '', phone: '', personal_message: '' }]);
        fetchMyInvites(); // Refresh stats
      } else {
        setMessage('Error sending invitations. Please try again.');
      }
    } catch (error) {
      setMessage('Error sending invitations. Please try again.');
    } finally {
      setSendingInvites(false);
    }
  };

  const addContact = () => {
    setContacts([...contacts, { name: '', email: '', phone: '', personal_message: '' }]);
  };

  const removeContact = (index) => {
    setContacts(contacts.filter((_, i) => i !== index));
  };

  const updateContact = (index, field, value) => {
    const updated = [...contacts];
    updated[index][field] = value;
    setContacts(updated);
  };

  const shareViaWebShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join Shot Clock Dating!',
          text: 'Check out this fun new speed dating app!',
          url: referralData.share_url
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      copyToClipboard(referralData.share_url);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded mb-4"></div>
          <div className="h-4 bg-gray-200 rounded mb-2"></div>
          <div className="h-4 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Users className="w-8 h-8 text-rose-600" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Invite Your Friends</h1>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Share Shot Clock Dating with your friends and help them find meaningful connections. 
          The more the merrier!
        </p>
      </div>

      {message && (
        <div className={`p-4 rounded-lg ${
          message.includes('Error') || message.includes('failed')
            ? 'bg-red-50 text-red-700 border border-red-200'
            : 'bg-green-50 text-green-700 border border-green-200'
        }`}>
          {message}
        </div>
      )}

      {/* Referral Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Share2 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Your Referral Code</h3>
              <p className="text-2xl font-bold text-blue-600">{referralData?.referral_code}</p>
            </div>
          </div>
        </div>

        {myInvites && (
          <>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Friends Joined</h3>
                  <p className="text-2xl font-bold text-green-600">{myInvites.stats.registered}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <Send className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Invites Sent</h3>
                  <p className="text-2xl font-bold text-orange-600">{myInvites.stats.total_sent}</p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Quick Share */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Share</h2>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              value={referralData?.share_url || ''}
              readOnly
              className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50"
              placeholder="Your referral link will appear here"
            />
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => copyToClipboard(referralData?.share_url)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
            >
              <Copy className="w-4 h-4" />
              <span>Copy</span>
            </button>
            <button
              onClick={shareViaWebShare}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2"
            >
              <Share2 className="w-4 h-4" />
              <span>Share</span>
            </button>
          </div>
        </div>
      </div>

      {/* Single Invite */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Invite a Friend</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <input
            type="text"
            placeholder="Friend's name"
            value={singleInvite.name}
            onChange={(e) => setSingleInvite(prev => ({ ...prev, name: e.target.value }))}
            className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
          />
          <input
            type="email"
            placeholder="Email address"
            value={singleInvite.email}
            onChange={(e) => setSingleInvite(prev => ({ ...prev, email: e.target.value }))}
            className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
          />
          <input
            type="tel"
            placeholder="Phone number (optional)"
            value={singleInvite.phone}
            onChange={(e) => setSingleInvite(prev => ({ ...prev, phone: e.target.value }))}
            className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
          />
        </div>
        <textarea
          placeholder="Personal message (optional)"
          value={singleInvite.personal_message}
          onChange={(e) => setSingleInvite(prev => ({ ...prev, personal_message: e.target.value }))}
          rows={3}
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 mb-4"
        />
        <button
          onClick={sendSingleInvite}
          disabled={sendingInvites}
          className="w-full bg-rose-600 text-white py-3 px-4 rounded-lg hover:bg-rose-700 disabled:opacity-50 flex items-center justify-center space-x-2"
        >
          <Mail className="w-4 h-4" />
          <span>{sendingInvites ? 'Sending...' : 'Send Invitation'}</span>
        </button>
      </div>

      {/* Bulk Invite */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Invite Multiple Friends</h2>
          <select
            value={inviteMethod}
            onChange={(e) => setInviteMethod(e.target.value)}
            className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500"
          >
            <option value="email">Email Only</option>
            <option value="sms">SMS Only</option>
            <option value="both">Email + SMS</option>
          </select>
        </div>

        {contacts.map((contact, index) => (
          <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4 p-4 border border-gray-200 rounded-lg">
            <input
              type="text"
              placeholder="Name"
              value={contact.name}
              onChange={(e) => updateContact(index, 'name', e.target.value)}
              className="p-2 border border-gray-300 rounded focus:ring-2 focus:ring-rose-500"
            />
            <input
              type="email"
              placeholder="Email"
              value={contact.email}
              onChange={(e) => updateContact(index, 'email', e.target.value)}
              className="p-2 border border-gray-300 rounded focus:ring-2 focus:ring-rose-500"
            />
            <input
              type="tel"
              placeholder="Phone"
              value={contact.phone}
              onChange={(e) => updateContact(index, 'phone', e.target.value)}
              className="p-2 border border-gray-300 rounded focus:ring-2 focus:ring-rose-500"
            />
            <button
              onClick={() => removeContact(index)}
              disabled={contacts.length === 1}
              className="p-2 text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}

        <div className="flex space-x-4">
          <button
            onClick={addContact}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Add Another</span>
          </button>
          <button
            onClick={sendBulkInvites}
            disabled={sendingInvites}
            className="flex-1 bg-rose-600 text-white py-2 px-4 rounded-lg hover:bg-rose-700 disabled:opacity-50 flex items-center justify-center space-x-2"
          >
            <Send className="w-4 h-4" />
            <span>{sendingInvites ? 'Sending...' : 'Send All Invitations'}</span>
          </button>
        </div>
      </div>

      {/* Recent Invitations */}
      {myInvites && myInvites.recent_invitations && myInvites.recent_invitations.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Invitations</h2>
          <div className="space-y-3">
            {myInvites.recent_invitations.map((invite, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">{invite.invitee_name}</p>
                  <p className="text-sm text-gray-600">
                    {invite.invitee_email || invite.invitee_phone} • 
                    Sent {new Date(invite.sent_at).toLocaleDateString()}
                  </p>
                </div>
                <div className={`px-2 py-1 rounded text-xs font-medium ${
                  invite.status === 'registered' ? 'bg-green-100 text-green-700' :
                  invite.status === 'opened' ? 'bg-blue-100 text-blue-700' :
                  invite.status === 'expired' ? 'bg-red-100 text-red-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {invite.status}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default InviteFriends;